import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { brainHealthAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { GoLiveDialog } from './GoLiveDialog';
import {
  Activity, AlertTriangle, CheckCircle2, Loader2, Radio, Mail, CreditCard, Clock,
} from 'lucide-react';

const ICONS = {
  simulated_mode: Radio,
  subscription_mismatch: CreditCard,
  mailbox_disconnected: Mail,
  cadences_overdue: Clock,
  credits_exhausted: CreditCard,
  credits_low: CreditCard,
};

const Metric = ({ label, value, tone = '' }) => (
  <div className="rounded-lg border bg-card px-3 py-2" data-testid={`brain-health-metric-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className={`text-lg font-semibold ${tone}`}>{value}</div>
    <div className="text-[11px] text-muted-foreground leading-tight">{label}</div>
  </div>
);

export const BrainHealthCard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(() => brainHealthAPI.peek());
  const [loading, setLoading] = useState(() => !brainHealthAPI.peek());
  const [goLiveOpen, setGoLiveOpen] = useState(false);

  useEffect(() => {
    let active = true;
    brainHealthAPI.getCached()
      .then((d) => { if (active && d) { setData(d); setLoading(false); } })
      .catch(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  if (loading) {
    return (
      <Card data-testid="brain-health-card-loading">
        <CardContent className="p-5 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking Brain activation status…
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const { is_live, activity = {}, blockers = [] } = data;
  const healthy = is_live && blockers.length === 0;

  return (
    <Card
      data-testid="brain-health-card"
      className={healthy ? 'border-emerald-500/30' : 'border-amber-500/40 bg-amber-500/[0.03]'}
    >
      <CardHeader className="pb-3 flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <Activity className={`h-4 w-4 ${healthy ? 'text-emerald-500' : 'text-amber-500'}`} />
          Brain Activation & Health
        </CardTitle>
        <Badge
          variant={is_live ? 'default' : 'secondary'}
          className={is_live ? 'bg-emerald-600' : 'bg-amber-600 text-white'}
          data-testid="brain-health-mode-badge"
        >
          {is_live ? 'LIVE' : 'SIMULATED'}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          <Metric label="Leads" value={activity.leads_total ?? 0} />
          <Metric label="Emails sent" value={activity.emails_sent ?? 0} />
          <Metric label="Drafts stuck" value={activity.drafts_stuck ?? 0} tone={activity.drafts_stuck ? 'text-amber-600' : ''} />
          <Metric label="Calls" value={activity.calls_made ?? 0} />
          <Metric label="Active cadences" value={activity.cadences_active ?? 0} />
          <Metric label="Overdue" value={activity.cadences_overdue ?? 0} tone={activity.cadences_overdue ? 'text-red-600' : ''} />
        </div>

        {healthy ? (
          <div className="flex items-center gap-2 text-sm text-emerald-600" data-testid="brain-health-ok">
            <CheckCircle2 className="h-4 w-4" /> Brain is LIVE and fully activated — outreach is running.
          </div>
        ) : (
          <div className="space-y-2" data-testid="brain-health-blockers">
            {blockers.map((b) => {
              const Icon = ICONS[b.code] || AlertTriangle;
              return (
                <div key={b.code} className="flex items-start gap-3 rounded-lg border bg-background p-3" data-testid={`brain-blocker-${b.code}`}>
                  <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${b.severity === 'high' ? 'text-red-500' : 'text-amber-500'}`} />
                  <p className="text-sm flex-1 text-foreground/90">{b.label}</p>
                  {(b.cta_path || b.code === 'simulated_mode') && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs shrink-0"
                      onClick={async () => {
                        if (b.code === 'simulated_mode') { setGoLiveOpen(true); return; }
                        if (b.cta_action === 'run_engine_now') {
                          try {
                            const r = await brainHealthAPI.runEngineNow();
                            const stats = r?.data?.data?.stats || r?.data?.stats || {};
                            brainHealthAPI.invalidate?.();
                            try {
                              const { toast } = await import('sonner');
                              toast.success(
                                `Engine forced — ${stats.cadences_executed || 0} cadence(s), ${stats.drafts_unstuck || 0} draft(s) unstuck`
                              );
                            } catch (_) {}
                          } catch (_e) {
                            try {
                              const { toast } = await import('sonner');
                              toast.error('Could not force engine — please retry in a minute.');
                            } catch (_) {}
                          }
                          return;
                        }
                        navigate(b.cta_path);
                      }}
                      data-testid={`brain-blocker-cta-${b.code}`}
                    >
                      {b.cta_label}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
      <GoLiveDialog open={goLiveOpen} onOpenChange={setGoLiveOpen} />
    </Card>
  );
};

export default BrainHealthCard;
